import { IamData } from 'features/iam/services/user-service';
import { ResponseTimeAnalytics, PlatformResourceChangedData } from '/models/websockets.types';

export interface Mention {
  userId: string;           // unique ID
  trigger: string;          // the actual text typed, e.g. "@john"
  start: number;            // where mention starts in content
  end: number;              // where mention ends in content
  user?: IamData;          // optional: prefetched user object for UI
}

export enum MessageRole {
  ADD_PARTICIPANT = 'add-participant',
  CONVERSATION_TITLE_UPDATED = 'conversation-title-updated',
  USER = 'user',
  ASSISTANT = 'assistant',
  USER_REMOVED = 'user-removed',
  USER_CHAT = 'user',
  USER_ASSISTANT_CHAT = 'user-assistant-chat',
  ASSISTANT_CHAT = 'assistant-chat',
  MODEL_THINKING = 'model-thinking',
  CONVERSATION_CREATED = 'conversation-created',
  APPROVAL = 'approval',
}

export enum FileGenerationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface Attachment {
  fileId: string;
  fileName: string;
  extension: string;
  sender: MessageRole;
  size?: number;
  status?: FileGenerationStatus;
}

export interface FileMetadata {
  fileId: string;
  fileName: string;
  extension: string;
  createdDate: string;
  lastUpdatedDate: string;
  size: number;
  sender: MessageRole;
}

export enum Tools {
  SEARCH = 'search',
  CREATE_IMAGE = 'create_image',
  VOICE = 'voice',
}

/** One retrieval chunk grouped under a source pill. Cited chunks resolve the deep-link page. */
export interface SourceChunk {
  cited?: boolean;
  pageNumber?: number;
  chunkNumber?: number;
  fileName?: string;
  [key: string]: unknown;
}

export interface Source {
  fileId: string;
  fileName: string;
  chunks: SourceChunk[];
  uri?: string | undefined;
  /** 1-based citation number resolving inline `[[ref:n]]` markers to this pill. */
  citationIndex?: number;
}

export interface SearchSource {
  pageContent: string;
  metadata: {
    title: string;
    url: string;
  }
}

export interface RelativeSearchQuestion {
  title: string;
  imgSrc: string | null;
  url: string | null;
  iframeSrc: string | null;
}

export interface ResponseAnalytics {
  timeToFirstToken: number;
  totalResponseTime: number;
  timePerOutputToken: number;
}

/** One assistant tool invocation returned on message metadata (e.g. exchange rate API). */
/** Skill attached to a user message (display + collaboration skill_ids). */
export interface MessageSkillMention {
  id: string;
  title: string;
  iconUrl?: string | null;
}

export interface MessageToolCall {
  toolId: string;
  toolName: string;
  arguments?: Record<string, unknown>;
  resultSummary?: string | null;
  status?: string;
  error?: string | null;
  durationMs?: number;
}

/** One subagent delegation from stream events or persisted AgentRun audit. */
export interface MessageSubagentRun {
  runId: string;
  subagentId: string;
  subagentName: string;
  status?: string;
  error?: string | null;
  durationMs?: number;
}

/** Persisted statuses (wire contract §5) plus client-derived 'skipped'. */
export type A2uiSurfaceStatus =
  | 'active'
  | 'answered'
  | 'approved'
  | 'declined'
  | 'expired'
  | 'skipped';

/** Grant breadth the BE offers for an approval; absent means approve-once only. */
export type ApprovalGrantScope = 'once' | 'chat' | 'always_action' | 'always_tool';

/** HIL approval state carried on an approval surface. */
export interface MessageA2uiApproval {
  approvalId: string;
  toolName?: string;
  toolArgs?: Record<string, unknown>;
  agentId?: string;
  subagentName?: string;
  initiatorUserId?: string;
  expiresAt?: string;
  decision?: 'approved' | 'declined';
  resolvedBy?: string;
  /** Scopes the user may grant. Undefined on older backends — approve-once only. */
  allowedScopes?: ApprovalGrantScope[];
  /** Total actions on the owning tool, for the "always allow this tool" label. */
  actionCount?: number;
}

/** Submitted answer for a surface, restored from a later user message's metadata.a2ui. */
export interface MessageA2uiSubmission {
  selections: Record<string, unknown>;
  instructions?: string;
}

/** One A2UI surface accumulated from A2UI_MESSAGE/APPROVAL_* chunks or persisted metadata.a2ui. */
export interface MessageA2uiSurface {
  initiatorUserId?: string;
  surfaceId: string;
  version: string;
  purpose: string;
  /** Accumulated raw A2UI spec messages, replayed into the renderer in order. */
  messages: Record<string, unknown>[];
  status: A2uiSurfaceStatus;
  approval?: MessageA2uiApproval;
  submission?: MessageA2uiSubmission;
  /** Answer length when this surface arrived on the stream — text after renders below. */
  contentOffset?: number;
  /**
   * `streamStatusEntries.length` when this surface arrived.
   * Steps before this index stay above the card (even when contentOffset is 0).
   */
  statusEntryCutoff?: number;
}

export type SkillEventType = 'used' | 'skipped' | 'resource_loaded';

/** One skill lifecycle event from stream or message metadata. */
export interface MessageSkillEvent {
  skillId: string;
  skillName: string;
  type: SkillEventType;
  version?: string;
  resourceName?: string;
  reason?: string;
  status?: string;
  error?: string | null;
  durationMs?: number;
}

/** Live socket phase shown in the assistant bubble before answer text arrives. */
export type ToolCallStreamStatus = 'started' | 'completed';

/** Agent todo item from write_todos / Update Todos tool args. */
export type StreamTodoStatus = 'pending' | 'in_progress' | 'completed';

export interface StreamTodoItem {
  content: string;
  status: StreamTodoStatus;
}

/** Live subagent delegation phase shown before answer text arrives. */
export type SubagentStreamStatus = 'started' | 'completed';

/** Conversation history compaction phase from CONVERSATION_COMPACTION_* socket events. */
export type CompactionStreamStatus = 'started' | 'completed' | 'failed';

/** Latest SKILL_* stream event shown until assistant text arrives. */
export type SkillStreamStatus = SkillEventType;

/** Icon variant for a live stream status row in the Working timeline. */
export type StreamStatusEntryIcon =
  | 'skill_used'
  | 'skill_resource'
  | 'skill_skipped'
  | 'tool_started'
  | 'tool_completed'
  | 'subagent_started'
  | 'subagent_completed'
  | 'processing_started';

/** One append-only status line shown under "Working" while the assistant streams. */
export interface StreamStatusEntry {
  id: string;
  kind: 'skill' | 'tool' | 'subagent' | 'system';
  label: string;
  icon: StreamStatusEntryIcon;
  /** Character offset in the answer where this status's Steps wave is shown. */
  contentOffset?: number;
  /** Populated for write_todos / Update Todos — renders an inline todo list. */
  todos?: StreamTodoItem[];
}

/** One item on the assistant-turn timeline. Socket order is the list order. */
export type StreamBlock =
  | { type: 'text'; text: string }
  | { type: 'steps'; entries: StreamStatusEntry[] }
  | { type: 'approval'; surface: MessageA2uiSurface }
  | { type: 'a2ui'; surface: MessageA2uiSurface };

export interface MessageGeneration {
  id: string;
  content: string;
  createdDate?: number;
  /** Display/local time string: backend `createdDate` as received, or `formatShowMessageTimeForSend` when sending. */
  showMessageTime?: string;
  thinkContent?: string;
  feedback?: boolean;
  attachments?: Attachment[];
  generatedImages?: Attachment[];
  HasFileGeneratedResponse?: boolean;
  sources?: Source[];
  searchSources?: SearchSource[];
  isThinking?: boolean;
  relatedSearcheQuestionsList?: RelativeSearchQuestion[];
  relatedSearcheImageList?: RelativeSearchQuestion[];
  relatedSearcheVideoList?: RelativeSearchQuestion[];
  hasSearchQuery?: boolean;
  isSkeletonShowForImages?: boolean;
  isSkeletonShowForVideos?: boolean;
  isSkeletonShowForQuestions?: boolean;
  isSearchSourcesShow?: boolean;
  isAnalyzed?: boolean;
  isAnalyzing?: boolean;
  codeExecutionSteps?: any[];
  parentIds?: string[];
  parentUserGenerationId?: string;
  parentMessage?: {
    senderName: string;
    content: string;
    time: number;
  };
  mentions?: Mention[];
  responseAnalytics?: ResponseTimeAnalytics;
  usedModelId?: string;
  metadata?: Record<string, any>;
  isThisGenerationOnGoing?: boolean;
  isCancelled?: boolean;
  /** Turn paused on an `auth_required` MCP-service event; row shows "Authenticating…" until resume. */
  pausedForAuth?: boolean;
  toolCalls?: MessageToolCall[];
  skillEvents?: MessageSkillEvent[];
  subagentRuns?: MessageSubagentRun[];
  /** A2UI surfaces rendered beneath the markdown content (keyed by surfaceId). */
  a2uiSurfaces?: MessageA2uiSurface[];
  /** Transient UI while streaming: replaces loader until {@link MessageGeneration.content} arrives. */
  toolCallStreamStatus?: ToolCallStreamStatus;
  /** Active tool name for streaming status copy (from TOOL_CALL_* socket events). */
  activeToolCallName?: string;
  subagentStreamStatus?: SubagentStreamStatus;
  /** Active subagent name for streaming status copy (from subagent.* socket events). */
  activeSubagentName?: string;
  skillStreamStatus?: SkillStreamStatus;
  /** Active skill name for streaming status copy (from SKILL_* socket events). */
  activeSkillName?: string;
  activeSkillResourceName?: string;
  /** Live compaction phase from CONVERSATION_COMPACTION_* socket events. */
  compactionStreamStatus?: CompactionStreamStatus;
  /** Append-only timeline of skill/tool activity while streaming (cleared when content arrives). */
  streamStatusEntries?: StreamStatusEntry[];
  /** Socket-ordered turn timeline (text, Steps, approvals, A2UI). */
  streamBlocks?: StreamBlock[];
  /** First reconnect LLM_ANSWER should replace stale history text so replay starts at the top. */
  needsReplayAnswerReplace?: boolean;
  /** Set when a guardrail content filter violation chunk terminates this turn with a refusal. */
  isContentFilterViolation?: boolean;
  /** Platform resources changed during this turn (e.g. created agent). */
  platformResourceChanges?: PlatformResourceChangedData[];
}

/** `auth_required` MCP service (now a Tool) chat-stream event payload. */
export interface McpServiceAuthRequiredData {
  mcpServiceId: string;
  mcpServiceName: string;
  /**
   * "password" → native creds modal; "oidc" → open authorizationUrl popup.
   * Anything else has no connect flow here (e.g. "host_only", "host_signin_required",
   * "host_token_rejected" — credentials the embedding site owns) and is surfaced as
   * {@link McpServiceAuthRequiredData.message} instead.
   */
  authMethod?: string;
  authorizationUrl?: string;
  conversationId?: string;
  /** Server-authored copy for methods we cannot connect from here. */
  message?: string;
}

/** Auth methods this app can actually complete from the chat. */
export const CONNECTABLE_AUTH_METHODS = ['password', 'oidc'];

export const isConnectableAuthMethod = (authMethod?: string): boolean =>
  !authMethod || CONNECTABLE_AUTH_METHODS.includes(authMethod);

export enum MessageDeliveryStatus {
  PENDING = 'pending',
  DELIVERED = 'delivered',
  FAILED = 'failed',
}

export interface Message {
  role: MessageRole;
  recordId: string;
  userRecordId?: string; // For assistant messages, keeps reference to the user's recordId
  idToReplaceWith?: string;
  userMessage?: {
    userName: string;
    message: string;
  };
  activeGenerationIndex?: number;
  toolsStatus?: Record<Tools, boolean>;
  senderId?: string;
  generations: MessageGeneration[];
  deliveryStatus?: MessageDeliveryStatus; // Only for user messages in group chats
}

export interface ReplyMessageData {
  generation: MessageGeneration;
  repliedUser?: {
    itemId: string;
    profileImageUrl: string | null;
    firstName: string;
    lastName: string | null;
    email: string;
    [key: string]: any;
  };
}

export interface VectorUploadItem {
  fileId: string;
}

export enum ButtonIds {
  AGENTS,
  SEARCH,
  CREATE_IMAGE,
  WRITE_OR_EDIT,
  VOICE,
}

export const initialFeaturesState: Record<ButtonIds, boolean> = {
  [ButtonIds.AGENTS]: false,
  [ButtonIds.SEARCH]: false,
  [ButtonIds.CREATE_IMAGE]: false,
  [ButtonIds.WRITE_OR_EDIT]: false,
  [ButtonIds.VOICE]: false,
};